import { type FC } from "react";
import { Button, Card, CardContent, Stack, TextField } from "@mui/material";
import { tokens } from "src/locales/tokens";
import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { useDispatch, useSelector } from "src/store";
import toast from "react-hot-toast";
import { thunks as storylineCategoryThunks } from "src/thunks/storyline-category";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import LoadingButton from "@mui/lab/LoadingButton";
import { RouterLink } from "src/components/core/link";
import { paths } from "src/paths";
import { createRoute } from "react-router-url-params";
import { useRouter } from "src/hooks/use-router";

// Define the Zod schema
const createValidationSchema = (t: any) =>
  z.object({
    name: z
      .string()
      .max(255, t(tokens.general.validators.maxLength255) as string)
      .min(1, t(tokens.general.validators.required) as string),
    description: z
      .string()
      .max(1000, t(tokens.general.validators.maxLength1000) as string)
      .min(1, t(tokens.general.validators.required) as string),
  });

// Define the Values type using Zod inference
type FormValues = z.infer<ReturnType<typeof createValidationSchema>>;

interface CreateStorylineCategoryFormProps {
  storylineId: string;
}

export const CreateStorylineCategoryForm: FC<
  CreateStorylineCategoryFormProps
> = (props) => {
  const { storylineId } = props;
  const { t } = useTranslation();
  const { strategyId } = useSelector((state) => state.settings);
  const dispatch = useDispatch();
  const router = useRouter();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Create the validation schema
  const validationSchema = createValidationSchema(t);

  // Initialize the form
  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<FormValues>({
    defaultValues: {
      name: "",
      description: "",
    },
    resolver: zodResolver(validationSchema),
  });

  const onSubmit = async (data: FormValues): Promise<void> => {
    setIsSubmitting(true);
    try {
      const category = await dispatch(
        storylineCategoryThunks.createStorylineCategory(
          strategyId!,
          storylineId,
          {
            name: data.name,
            description: data.description,
          }
        )
      );
      toast.success(
        t(tokens.storylinesCategories.create.form.successMessage)
      );

      router.push(
        createRoute(paths.storylineCategories.details).link({
          storylineId: storylineId,
          categoryId: category.id,
        })
      );
    } catch (error) {
      toast.error(t(tokens.general.formError));
      setError("root", { message: "Submission failed" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.name}
                    fullWidth
                    helperText={errors.name?.message}
                    label={t(tokens.storylinesCategories.create.form.name)}
                  />
                )}
              />
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.description}
                    fullWidth
                    helperText={errors.description?.message}
                    label={t(tokens.storylinesCategories.create.form.description)}
                  />
                )}
              />
            </Stack>
          </CardContent>
        </Card>

        <Stack
          direction="row"
          alignItems="start"
          justifyContent="flex-start"
          spacing={1}
        >
          <LoadingButton
            type="submit"
            disabled={isSubmitting}
            loading={isSubmitting}
            variant="contained"
          >
            {t(tokens.general.buttons.create)}
          </LoadingButton>
          <Button
            component={RouterLink}
            href={createRoute(paths.storylines.details).link({
              storylineId: storylineId,
            })}
            color="inherit"
          >
            {t(tokens.general.buttons.cancel)}
          </Button>
        </Stack>
      </Stack>
    </form>
  );
};
